export type RuntimeValue = string | number | boolean | null | undefined | object;
export type ExtendedRuntimeValue = RuntimeValue | bigint | symbol;
export type ObjectRecordValue = ExtendedRuntimeValue;
export type ObjectRecord = Record<string, ObjectRecordValue>;
export function isObjectRecord(value: unknown): value is ObjectRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function isString(value: unknown): value is string {
  return typeof value === 'string';
}

export function isNumber(value: unknown): value is number {
  return typeof value === 'number';
}

export function isBoolean(value: unknown): value is boolean {
  return typeof value === 'boolean';
}

type PrimitiveTypeMap = {
  string: string;
  number: number;
  boolean: boolean;
};
function getTypedProperty<K extends keyof PrimitiveTypeMap>(
  value: unknown,
  key: string,
  expected: K,
): PrimitiveTypeMap[K] | undefined {
  if (!isObjectRecord(value)) {
    return undefined;
  }
  const propertyValue = value[key];
  return typeof propertyValue === expected ? (propertyValue as PrimitiveTypeMap[K]) : undefined;
}
export function getStringProperty(value: unknown, key: string): string | undefined {
  return getTypedProperty(value, key, 'string');
}
export function getNumberProperty(value: unknown, key: string): number | undefined {
  return getTypedProperty(value, key, 'number');
}
export function getBooleanProperty(value: unknown, key: string): boolean | undefined {
  return getTypedProperty(value, key, 'boolean');
}
export function isDefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

export function isNonNull<T>(value: T | null): value is T {
  return value !== null;
}

export function isArrayOf<T>(value: unknown, predicate: (item: unknown) => item is T): value is T[] {
  return Array.isArray(value) && value.every(predicate);
}

export function isOptionalArrayOf<T>(
  value: unknown,
  predicate: (item: unknown) => item is T,
): value is T[] | null | undefined {
  return value === undefined || value === null || isArrayOf(value, predicate);
}

export function isStringRecord(value: unknown): value is Record<string, string> {
  if (!isObjectRecord(value)) {
    return false;
  }
  for (const key in value) {
    if (typeof value[key] !== 'string') {
      return false;
    }
  }
  return true;
}

export function hasStringField<K extends string>(
  value: Record<string, unknown>,
  key: K,
): value is Record<K, string> & Record<string, unknown> {
  return typeof value[key] === 'string';
}
export function hasNumberField<K extends string>(
  value: Record<string, unknown>,
  key: K,
): value is Record<K, number> & Record<string, unknown> {
  return typeof value[key] === 'number';
}
export function hasBooleanField<K extends string>(
  value: Record<string, unknown>,
  key: K,
): value is Record<K, boolean> & Record<string, unknown> {
  return typeof value[key] === 'boolean';
}
export function hasOptionalStringField<K extends string>(
  value: Record<string, unknown>,
  key: K,
): value is Partial<Record<K, string>> & Record<string, unknown> {
  return value[key] === undefined || typeof value[key] === 'string';
}
export function hasOptionalNumberField<K extends string>(
  value: Record<string, unknown>,
  key: K,
): value is Partial<Record<K, number>> & Record<string, unknown> {
  return value[key] === undefined || typeof value[key] === 'number';
}
export function hasOptionalNumericField<K extends string>(
  value: Record<string, unknown>,
  key: K,
): value is Partial<Record<K, number | string>> & Record<string, unknown> {
  return value[key] === undefined || typeof value[key] === 'number' || typeof value[key] === 'string';
}
export function hasOptionalBooleanField<K extends string>(
  value: Record<string, unknown>,
  key: K,
): value is Partial<Record<K, boolean>> & Record<string, unknown> {
  return value[key] === undefined || typeof value[key] === 'boolean';
}
export function hasOptionalLiteralField<K extends string, L extends string>(
  value: Record<string, unknown>,
  key: K,
  literals: readonly L[],
): value is Partial<Record<K, L>> & Record<string, unknown> {
  const v = value[key];
  return v === undefined || (typeof v === 'string' && (literals as readonly string[]).includes(v));
}

type Falsy = false | 0 | '' | null | undefined;
export function isTruthy<T>(value: T): value is Exclude<T, Falsy> {
  return Boolean(value);
}
